import type { GLink, GNode } from "@/stores/graph";
import {
  CANVAS_BACKGROUND,
  CANVAS_FOREGROUND,
  CANVAS_MUTED,
  CANVAS_PRIMARY,
  CANVAS_PRIMARY_BRIGHT,
  EDGE_COLOR,
  NODE_COLOR,
  NODE_RADIUS,
} from "@/lib/palette";

export interface PaintCtx {
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  hoverNodeId: string | null;
  /** ids adjacent to the hovered node — everything else dims (Obsidian-style) */
  neighborIds: Set<string> | null;
  /** pinned nodes — tinted differently WHEN a label already shows (hover or
   * select). Pinning does not make a label appear on its own. */
  pinnedIds: Set<string> | null;
  /** multi-selected node ids (rubber-band selection) */
  multiSelected: Set<string> | null;
  flash: Map<string, number>;
  now: number;
  /**
   * Rects already claimed by a label THIS frame, in screen space.
   *
   * The old rule was `showLabel = hovered || neighbour`, which on a hub with 30
   * edges painted 31 plates at once, each 50% opaque, all pinned directly under
   * nodes the layout has already packed together. The pile was not a styling
   * problem; it was the rule. Now a label must claim a rect, and a label that
   * would overlap one already claimed is dropped — the neighbours are read in
   * the side rail instead, where they have room to be read.
   *
   * The host clears this at the start of every frame.
   */
  labelRects: { x: number; y: number; w: number; h: number }[];
  /** How many labels may still be drawn this frame. */
  labelBudget: number;
  /**
   * The one edge under the cursor, if any.
   *
   * Edge labels used to key off `hoverNodeId`: every edge TOUCHING the hovered
   * node drew its name at its own midpoint. On a hub those midpoints all radiate
   * from the same point, so hovering a 30-edge node stacked 30 words on top of
   * each other — the node labels were fixed and this was the pile that was left.
   * An edge names itself when you point at THAT edge, or when it is selected.
   */
  hoverLinkId: string | null;
}

/** Does `r` overlap anything already claimed? Linear, but the budget keeps the
 *  list to a dozen entries, so this is cheaper than any spatial structure. */
function labelFits(
  r: { x: number; y: number; w: number; h: number },
  taken: { x: number; y: number; w: number; h: number }[],
): boolean {
  for (const t of taken) {
    if (r.x < t.x + t.w && t.x < r.x + r.w && r.y < t.y + t.h && t.y < r.y + r.h) return false;
  }
  return true;
}

export function nodeRadius(node: GNode): number {
  return NODE_RADIUS[node.type] ?? 4;
}

/** Pre-rendered radial glow sprites stand in for per-node `ctx.shadowBlur`.
 * A canvas shadow blur is a gaussian filter — one of the most expensive 2D
 * ops — and the old paint set it for EVERY node on EVERY frame, making the
 * glow the dominant O(N) atlas paint cost. We rasterize one translucent halo
 * per node colour ONCE and stamp it with a cheap drawImage; the hovered /
 * selected node still gets a real, stronger shadow blur (only a node or two,
 * so its cost is negligible). Cached per colour; nulls cached too so a
 * non-browser/jsdom context (no 2d canvas) silently skips the glow. */
const GLOW_TEX = 64;
const glowCache = new Map<string, HTMLCanvasElement | null>();

function glowSprite(color: string): HTMLCanvasElement | null {
  const cached = glowCache.get(color);
  if (cached !== undefined) return cached;
  let sprite: HTMLCanvasElement | null = null;
  try {
    const c = document.createElement("canvas");
    c.width = c.height = GLOW_TEX;
    const g = c.getContext("2d");
    if (g) {
      const r = GLOW_TEX / 2;
      const grad = g.createRadialGradient(r, r, 0, r, r, r);
      grad.addColorStop(0, color);
      grad.addColorStop(0.4, color);
      grad.addColorStop(1, "rgba(0,0,0,0)");
      g.globalAlpha = 0.5;
      g.fillStyle = grad;
      g.beginPath();
      g.arc(r, r, r, 0, 2 * Math.PI);
      g.fill();
      sprite = c;
    }
  } catch {
    sprite = null; // no 2d canvas (jsdom/SSR): skip the glow, the disc still draws
  }
  glowCache.set(color, sprite);
  return sprite;
}

/** Star-chart node: soft glow disc; suggested nodes are hollow proposals;
 * fresh nodes pulse a violet ring; document labels render in Fraunces. */
export function paintNode(
  node: GNode,
  ctx: CanvasRenderingContext2D,
  scale: number,
  p: PaintCtx,
): void {
  const x = node.x ?? 0;
  const y = node.y ?? 0;
  const color = NODE_COLOR[node.type] ?? "#A8A18A";
  const r = nodeRadius(node);
  const multi = p.multiSelected?.has(node.id) ?? false;
  const selected = p.selectedNodeId === node.id || multi;
  const hovered = p.hoverNodeId === node.id;
  const pinned = p.pinnedIds?.has(node.id) ?? false;
  // Obsidian-style hover focus: dim everything not adjacent to the hovered node.
  const dim = p.hoverNodeId != null && !hovered && !(p.neighborIds?.has(node.id) ?? false);
  ctx.globalAlpha = dim ? 0.18 : 1;

  // Violet pulse for freshly-arrived nodes (two expanding rings). `now === 0` is
  // the sentinel for "not animating" (tab hidden, no active flash, or per-frame
  // effects disabled on a coarse pointer) — skip the time-based ring entirely so
  // we neither animate a stale flash nor force a live timestamp every frame.
  const flashedAt = p.flash.get(node.id);
  if (flashedAt !== undefined && p.now > 0) {
    const t = (p.now - flashedAt) / 1200; // 0..~2 over 2.4s
    const phase = t % 1;
    if (t < 2.2) {
      ctx.beginPath();
      ctx.arc(x, y, r + 3 + phase * 14, 0, 2 * Math.PI);
      ctx.strokeStyle = `rgba(${CANVAS_PRIMARY}, ${0.5 * (1 - phase)})`;
      ctx.lineWidth = 1.4 / scale;
      ctx.stroke();
    }
  }

  // Organizational hubs are SQUARES; everything else is a disc.
  const square = node.type === "org";
  const tracePath = (radius: number) => {
    ctx.beginPath();
    if (square) ctx.rect(x - radius, y - radius, radius * 2, radius * 2);
    else ctx.arc(x, y, radius, 0, 2 * Math.PI);
  };

  // Glow: the hovered/selected node gets a real, stronger canvas blur (a node
  // or two — trivial cost); every other node gets a cheap pre-rendered halo
  // sprite instead of a per-node, per-frame shadowBlur.
  const focused = hovered || selected;
  if (focused) {
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 16;
  } else {
    const sprite = glowSprite(color);
    if (sprite) {
      const gr = r + 6; // halo extent ~ the old shadowBlur:7 spread
      ctx.drawImage(sprite, x - gr, y - gr, gr * 2, gr * 2);
    }
  }

  tracePath(r + (selected ? 1 : 0));
  if (node.status === "suggested") {
    // Hollow: a proposal awaiting confirmation.
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.4;
    ctx.setLineDash([2.5, 2]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = `rgba(${CANVAS_BACKGROUND}, 0.85)`;
    ctx.fill();
  } else {
    ctx.fillStyle = color;
    ctx.fill();
  }
  if (focused) ctx.restore();

  if (selected) {
    tracePath(r + 3.5);
    ctx.strokeStyle = `rgba(${CANVAS_PRIMARY}, 0.85)`;
    ctx.lineWidth = 1.2 / scale;
    ctx.stroke();
  }

  // A node names itself only when SELECTED.
  //
  // Hovering used to paint a label too, which was the last routine source of
  // text on the canvas: move the cursor across a cluster and names flick on and
  // off under it, each one landing wherever its node happens to sit. The hover
  // answer is now a CARD — `NodeHoverCard`, the same card the homepage draws —
  // which holds the title, the folder path, the description and the preview
  // without competing with the atlas for the same pixels. Selection is the
  // durable statement ("I care about these"), so it keeps its label, and it is
  // still subject to the per-frame budget and the collision test.
  const showLabel = selected;
  if (showLabel && p.labelBudget > 0) {
    const label = node.label.length > 34 ? node.label.slice(0, 33) + "…" : node.label;
    // SCREEN pixels, not world units. `10.5 / scale` grew the text as you zoomed
    // OUT, so the labels got bigger exactly as the nodes crowded together.
    const fontSize = 12 / scale;
    ctx.font =
      node.type === "document"
        ? `italic 500 ${fontSize * 1.18}px Fraunces, Georgia, serif`
        : `400 ${fontSize}px "Spline Sans Mono", monospace`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    // Claim a rect, or do not draw. A dropped label is better than an
    // unreadable one, and the rail has the name either way.
    const tw = ctx.measureText(label).width;
    const th = fontSize * 1.35;
    const pad = 3 / scale;
    const box = {
      x: x - tw / 2 - pad,
      y: y + r + 3 / scale,
      w: tw + pad * 2,
      h: th + pad,
    };
    if (!labelFits(box, p.labelRects)) {
      ctx.globalAlpha = 1;
      return;
    }
    p.labelRects.push(box);
    p.labelBudget -= 1;

    // OPAQUE, with padding and a hairline. At 50% alpha two labels that did
    // overlap turned into mud; a label that survives the collision test has
    // earned a background you can actually read it against.
    ctx.fillStyle = `rgba(${CANVAS_BACKGROUND}, 0.94)`;
    ctx.beginPath();
    ctx.rect(box.x, box.y, box.w, box.h);
    ctx.fill();
    ctx.strokeStyle = `rgba(${CANVAS_MUTED}, 0.35)`;
    ctx.lineWidth = 1 / scale;
    ctx.stroke();

    ctx.fillStyle =
      hovered || selected
        ? `rgba(${CANVAS_FOREGROUND}, 0.95)`
        : node.type === "document"
          ? `rgba(${CANVAS_FOREGROUND}, 0.82)`
          : node.type === "org"
            ? "rgba(122,222,158,0.88)"
            : pinned
              ? `rgba(${CANVAS_PRIMARY_BRIGHT}, 0.85)`
              : `rgba(${CANVAS_MUTED}, 0.8)`;
    ctx.fillText(label, x, box.y + pad / 2);
  }
  ctx.globalAlpha = 1;
}

function linkTouches(link: GLink, nodeId: string): boolean {
  const s = typeof link.source === "object" ? link.source.id : link.source;
  const t = typeof link.target === "object" ? link.target.id : link.target;
  return s === nodeId || t === nodeId;
}

/**
 * Where a connection came from — the question the atlas never answered.
 *
 * Every line looked the same. A link the user drew by hand, a link the parser
 * derived from the folder layout, and a link a model guessed at 31% confidence
 * were three different kinds of claim rendered as one grey stroke, so the
 * graph could not be read critically: you could not tell your own reasoning
 * from the machine's. Three classes, three treatments:
 *
 *   authored  — you asserted it. Solid, full weight, full colour.
 *   derived   — the parser or the vault layout implies it. Solid, thin, quiet;
 *               true, but it is structure, not insight.
 *   inferred  — a model proposed it. Dashed, and its opacity IS its confidence,
 *               so a weak guess looks weak.
 */
export type EdgeClass = "authored" | "derived" | "inferred";

export function edgeClass(link: GLink): EdgeClass {
  if (link.edge.origin === "user") return "authored";
  if (link.edge.origin === "llm") return "inferred";
  return "derived";
}

/** Blend an edge-type hex toward the canvas background to quiet it down,
 *  rather than using globalAlpha — force-graph hands us a colour string, not a
 *  draw call, so the fade has to live in the colour itself. */
function fade(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, "$1$1") : h, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(3)})`;
}

/** An inferred edge's strength, in [0, 1]. A null confidence on an LLM edge
 *  should be impossible (the ontology refuses it), so treat it as a weak guess
 *  rather than a strong one — an unknown is not a certainty. */
export function edgeStrength(link: GLink): number {
  return link.edge.confidence ?? 0.35;
}

export function linkColor(link: GLink, p: PaintCtx): string {
  if (p.selectedEdgeId === link.id) return `rgba(${CANVAS_PRIMARY}, 0.9)`;
  // Dim links not connected to the hovered node.
  if (p.hoverNodeId != null && !linkTouches(link, p.hoverNodeId)) return "rgba(45,45,48,0.22)";
  const base = EDGE_COLOR[link.edge.type] ?? "#333338";
  switch (edgeClass(link)) {
    case "authored":
      return base;
    case "inferred":
      // An unconfirmed proposal wears the primary so it reads as an OFFER, not
      // a fact. Either way the opacity tracks the model's own confidence.
      return link.edge.status === "suggested"
        ? `rgba(${CANVAS_PRIMARY}, ${(0.2 + 0.5 * edgeStrength(link)).toFixed(3)})`
        : fade(base, 0.35 + 0.6 * edgeStrength(link));
    default:
      return fade(base, 0.62);
  }
}

export function linkWidth(link: GLink, p: PaintCtx): number {
  if (p.selectedEdgeId === link.id) return 2;
  if (edgeClass(link) === "authored") return 1.5;
  // Structure is the loudest thing in a real vault by sheer count — a folder
  // of 20 files is 190 co_located edges. It gets the thinnest line.
  return link.edge.type === "contains" || link.edge.type === "co_located" ? 0.6 : 1;
}

export function linkDash(link: GLink): number[] | null {
  if (edgeClass(link) !== "inferred") return null;
  // A confirmed guess is still a guess, so it stays dashed; accepting it only
  // lengthens the dash, from "proposed" to "kept".
  return link.edge.status === "suggested" ? [2.5, 3] : [5, 2.5];
}

/** What an edge "says": its human label if set, else its (humanized) type. */
export function linkText(link: GLink): string {
  const label = link.edge.label?.trim();
  return label || link.edge.type.replace(/_/g, " ");
}

/** Draw an edge's label at its midpoint — for the ONE edge under the cursor,
 * or the selected one.
 *
 * The old rule was "selected, or touching the hovered node", and on a hub that
 * is thirty labels whose midpoints all converge on the same place: the single
 * worst pile of text the atlas produced. Pointing at an edge is an unambiguous
 * request to read that edge; hovering its endpoint is not.
 *
 * Runs in linkCanvasObject mode "after" (on top of the line). */
export function paintLinkLabel(
  link: GLink,
  ctx: CanvasRenderingContext2D,
  scale: number,
  p: PaintCtx,
): void {
  const selected = p.selectedEdgeId === link.id;
  const pointed = p.hoverLinkId != null && p.hoverLinkId === link.id;
  if (!selected && !pointed) return;
  const s = link.source;
  const t = link.target;
  if (typeof s !== "object" || typeof t !== "object") return;
  const x = ((s.x ?? 0) + (t.x ?? 0)) / 2;
  const y = ((s.y ?? 0) + (t.y ?? 0)) / 2;
  const text = linkText(link);
  if (!text) return;

  const fontSize = Math.max(9 / scale, 2.4);
  ctx.save();
  ctx.font = `500 ${fontSize}px "Spline Sans Mono", monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const w = ctx.measureText(text).width;
  ctx.fillStyle = `rgba(${CANVAS_BACKGROUND}, 0.82)`;
  ctx.fillRect(x - w / 2 - 2, y - fontSize * 0.8, w + 4, fontSize * 1.6);
  ctx.fillStyle = selected ? `rgba(${CANVAS_PRIMARY}, 0.95)` : `rgba(${CANVAS_FOREGROUND}, 0.9)`;
  ctx.fillText(text, x, y);
  ctx.restore();
}
